import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Plus, Trash2, Save, Image, X, Lock, LogOut, Eye, EyeOff, Loader2, RefreshCw, Video, Film, Package, Truck, CheckCircle, Clock, DollarSign, Zap, Settings, Hash, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useCollection } from '../context/CollectionContext';
import Logo from '../components/ui/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CHANGE YOUR PASSWORD HERE
// ============================================
const ADMIN_PASSWORD = 'Paige$Minerals2024!';
// ============================================

const AdminPage = () => {
    const { 
        minerals, shopItems, 
        updateMineral, addMineral, deleteMineral, 
        updateShopItem, addShopItem, deleteShopItem,
        siteSettings, updateSiteSettings,
        uploadImage, uploadVideo, refreshData,
        isLoading, error
    } = useCollection();
    
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [activeTab, setActiveTab] = useState('collection');
    const [editingItem, setEditingItem] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    
    // Orders state
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orderStats, setOrderStats] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    
    // Newsletter subscribers state
    const [subscribers, setSubscribers] = useState([]);
    const [subscribersLoading, setSubscribersLoading] = useState(false);
    
    // Carousel settings
    const [carouselSettings, setCarouselSettings] = useState({
        autoplaySpeed: 5000,  // milliseconds
        transitionDuration: 500,  // milliseconds
        maxSlides: 6,
        enableAutoplay: true
    });
    
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        size: '',
        weight: '',
        acquisitionDate: '',
        description: '',
        rarity: 'Fine',
        image: '',
        images: [],  // Gallery images array
        videos: [],  // Videos array
        price: '',
        category: 'Specimens',
        // Inventory fields for shop items
        sku: '',
        stock_quantity: 1,
        stock_status: 'in_stock'
    });

    // Generate SKU from item name
    const generateSKU = () => {
        if (!formData.name) {
            toast.error('Enter a name first to generate SKU');
            return;
        }
        // Get first 3 letters of name (uppercase), add random 3 digits
        const prefix = formData.name.replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase();
        const suffix = Math.floor(100 + Math.random() * 900);
        const newSKU = `${prefix}-${suffix}`;
        setFormData(prev => ({ ...prev, sku: newSKU }));
        toast.success(`SKU generated: ${newSKU}`);
    };

    // Check if already authenticated
    useEffect(() => {
        const auth = sessionStorage.getItem('paige_admin_auth');
        if (auth === 'true') {
            setIsAuthenticated(true);
        }
    }, []);

    // Fetch orders when orders tab is active
    useEffect(() => {
        if (isAuthenticated && activeTab === 'orders') {
            fetchOrders();
            fetchOrderStats();
        }
    }, [isAuthenticated, activeTab]);

    // Fetch subscribers when subscribers tab is active
    useEffect(() => {
        if (isAuthenticated && activeTab === 'subscribers') {
            fetchSubscribers();
        }
    }, [isAuthenticated, activeTab]);

    const fetchOrders = async () => {
        setOrdersLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/orders`);
            if (response.ok) {
                const data = await response.json();
                setOrders(data);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Failed to load orders');
        } finally {
            setOrdersLoading(false);
        }
    };

    const fetchOrderStats = async () => {
        try {
            const response = await fetch(`${API_URL}/api/orders/stats/summary`);
            if (response.ok) {
                const data = await response.json();
                setOrderStats(data);
            }
        } catch (error) {
            console.error('Error fetching order stats:', error);
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const response = await fetch(`${API_URL}/api/orders/${orderId}/status?status=${newStatus}`, {
                method: 'PUT'
            });
            if (response.ok) {
                toast.success('Order status updated');
                fetchOrders();
                fetchOrderStats();
            } else {
                throw new Error('Failed to update');
            }
        } catch (error) {
            toast.error('Failed to update order status');
        }
    };

    const fetchSubscribers = async () => {
        setSubscribersLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribers`);
            if (response.ok) {
                const data = await response.json();
                setSubscribers(data);
            }
        } catch (error) {
            console.error('Error fetching subscribers:', error);
            toast.error('Failed to load subscribers');
        } finally {
            setSubscribersLoading(false);
        }
    };

    const exportSubscribers = () => {
        if (subscribers.length === 0) {
            toast.error('No subscribers to export');
            return;
        }
        const emails = subscribers.map(s => s.email).join('\n');
        const blob = new Blob([emails], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'newsletter_subscribers.txt';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Subscribers exported!');
    };

    const deleteSubscriber = async (email) => {
        if (!window.confirm(`Remove ${email} from subscribers?`)) return;
        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribers/${encodeURIComponent(email)}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toast.success('Subscriber removed');
                setSubscribers(prev => prev.filter(s => s.email !== email));
            } else {
                throw new Error('Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting subscriber:', error);
            toast.error('Failed to remove subscriber');
        }
    };

    const rarityOptions = ['', 'Fine', 'Rare', 'Exceptional', 'World-Class'];
    const categoryOptions = ['Specimens', 'Thumbnail', 'Miniature', 'Cabinet', 'Display', 'Small Cabinet', 'Large Cabinet'];

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
            sessionStorage.setItem('paige_admin_auth', 'true');
            toast.success('Welcome back!');
        } else {
            toast.error('Incorrect password');
        }
        setPassword('');
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        sessionStorage.removeItem('paige_admin_auth');
        toast.success('Logged out');
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            try {
                const url = await uploadImage(file);
                setFormData(prev => ({ ...prev, image: url }));
                toast.success('Image uploaded!');
            } catch (err) {
                toast.error('Failed to upload image');
                console.error(err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleGalleryImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            try {
                const url = await uploadImage(file);
                setFormData(prev => ({ 
                    ...prev, 
                    images: [...(prev.images || []), url] 
                }));
                toast.success('Gallery image added!');
            } catch (err) {
                toast.error('Failed to upload image');
                console.error(err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const removeGalleryImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToRemove)
        }));
        toast.success('Image removed from gallery');
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (max 500MB)
            const maxSize = 500 * 1024 * 1024;
            if (file.size > maxSize) {
                toast.error('Video too large', {
                    description: 'Maximum file size is 500MB'
                });
                return;
            }

            setIsUploading(true);
            toast.loading('Uploading video...', { id: 'video-upload' });
            
            try {
                const result = await uploadVideo(file);
                setFormData(prev => ({ 
                    ...prev, 
                    videos: [...(prev.videos || []), result.url] 
                }));
                toast.success(`Video uploaded (${result.sizeMb}MB)`, { id: 'video-upload' });
            } catch (err) {
                toast.error('Failed to upload video', { id: 'video-upload' });
                console.error(err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const removeVideo = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            videos: prev.videos.filter((_, index) => index !== indexToRemove)
        }));
        toast.success('Video removed');
    };

    const handleHeroImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            try {
                const url = await uploadImage(file);
                await updateSiteSettings({ heroImage: url });
                toast.success('Hero image updated!');
            } catch (err) {
                toast.error('Failed to update hero image');
                console.error(err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            try {
                const url = await uploadImage(file);
                await updateSiteSettings({ logo: url });
                toast.success('Logo updated!');
            } catch (err) {
                toast.error('Failed to update logo');
                console.error(err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (item, type) => {
        setEditingItem({ ...item, type });
        setFormData({
            name: item.name || '',
            location: item.location || '',
            size: item.size || '',
            weight: item.weight || '',
            acquisitionDate: item.acquisitionDate || '',
            description: item.description || '',
            rarity: item.rarity || '',
            image: item.image || '',
            images: item.images || [],
            videos: item.videos || [],
            price: item.price || '',
            category: item.category || 'Specimens',
            // Inventory fields
            sku: item.sku || '',
            stock_quantity: item.stock_quantity ?? 1,
            stock_status: item.stock_status || 'in_stock'
        });
        setIsAddingNew(false);
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
        setEditingItem(null);
        setFormData({
            name: '',
            location: '',
            size: '',
            weight: '',
            acquisitionDate: '',
            description: '',
            rarity: '',
            image: '',
            images: [],
            videos: [],
            price: '',
            category: 'Specimens',
            // Inventory fields
            sku: '',
            stock_quantity: 1,
            stock_status: 'in_stock'
        });
    };

    const handleSave = async () => {
        if (!formData.name || !formData.image) {
            toast.error('Please fill in required fields', {
                description: 'Name and image are required'
            });
            return;
        }

        setIsSaving(true);
        try {
            if (activeTab === 'collection') {
                if (isAddingNew) {
                    await addMineral({
                        ...formData,
                        forSale: false
                    });
                    toast.success('Specimen added to collection!');
                } else if (editingItem) {
                    await updateMineral(editingItem.id, formData);
                    toast.success('Specimen updated!');
                }
            } else if (activeTab === 'shop') {
                if (isAddingNew) {
                    await addShopItem({
                        ...formData,
                        price: parseFloat(formData.price) || 0,
                        stock_quantity: parseInt(formData.stock_quantity) || 1,
                        stock_status: formData.stock_status || 'in_stock'
                    });
                    toast.success('Item added to shop!');
                } else if (editingItem) {
                    await updateShopItem(editingItem.id, {
                        ...formData,
                        price: parseFloat(formData.price) || 0,
                        stock_quantity: parseInt(formData.stock_quantity) || 1,
                        stock_status: formData.stock_status || 'in_stock'
                    });
                    toast.success('Shop item updated!');
                }
            }

            setEditingItem(null);
            setIsAddingNew(false);
        } catch (err) {
            toast.error('Failed to save', {
                description: err.message
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id, type) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                if (type === 'collection') {
                    await deleteMineral(id);
                    toast.success('Specimen removed from collection');
                } else {
                    await deleteShopItem(id);
                    toast.success('Item removed from shop');
                }
                if (editingItem?.id === id) {
                    setEditingItem(null);
                }
            } catch (err) {
                toast.error('Failed to delete item');
            }
        }
    };

    const handleCancel = () => {
        setEditingItem(null);
        setIsAddingNew(false);
    };

    // Login Screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <div className="text-center mb-8">
                        <Link to="/">
                            <Logo className="h-20 mx-auto mb-6" />
                        </Link>
                        <h1 className="font-display text-3xl text-foreground italic mb-2">
                            Admin Access
                        </h1>
                        <p className="font-body text-sm text-muted-foreground">
                            Enter your password to continue
                        </p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="pl-10 pr-10 h-12 rounded-sm"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        <Button type="submit" className="w-full h-12 rounded-sm">
                            Sign In
                        </Button>
                    </form>

                    <p className="text-center mt-6">
                        <Link to="/" className="font-body text-sm text-muted-foreground hover:text-foreground">
                            ← Back to site
                        </Link>
                    </p>
                </motion.div>
            </div>
        );
    }

    const currentItems = activeTab === 'collection' ? minerals : shopItems;

    return (
        <div className="min-h-screen bg-background">
            {/* Admin Header */}
            <header className="bg-foreground text-background py-4 px-6">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <div className="w-8 h-8 bg-background rounded-sm flex items-center justify-center">
                                <span className="font-display text-foreground text-sm">P</span>
                            </div>
                        </Link>
                        <span className="font-body text-sm tracking-wider-custom uppercase">
                            Admin Portal
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={refreshData}
                            className="text-background hover:text-background hover:bg-background/10"
                            disabled={isLoading}
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Link to="/" className="font-body text-sm text-background/70 hover:text-background">
                            View Site
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLogout}
                            className="text-background hover:text-background hover:bg-background/10"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                {/* Tabs - Mobile Scrollable */}
                <div className="flex gap-1 sm:gap-4 mb-8 border-b border-border overflow-x-auto pb-px scrollbar-hide">
                    <button
                        onClick={() => { setActiveTab('collection'); handleCancel(); }}
                        className={`pb-3 px-3 sm:px-4 font-body text-xs sm:text-sm tracking-wider-custom uppercase transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === 'collection'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Collection ({minerals.length})
                    </button>
                    <button
                        onClick={() => { setActiveTab('shop'); handleCancel(); }}
                        className={`pb-3 px-3 sm:px-4 font-body text-xs sm:text-sm tracking-wider-custom uppercase transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === 'shop'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Shop ({shopItems.length})
                    </button>
                    <button
                        onClick={() => { setActiveTab('orders'); handleCancel(); }}
                        className={`pb-3 px-3 sm:px-4 font-body text-xs sm:text-sm tracking-wider-custom uppercase transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === 'orders'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Orders {orders.length > 0 && `(${orders.length})`}
                    </button>
                    <button
                        onClick={() => { setActiveTab('subscribers'); handleCancel(); }}
                        className={`pb-3 px-3 sm:px-4 font-body text-xs sm:text-sm tracking-wider-custom uppercase transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === 'subscribers'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Subscribers {subscribers.length > 0 && `(${subscribers.length})`}
                    </button>
                    <button
                        onClick={() => { setActiveTab('settings'); handleCancel(); }}
                        className={`pb-3 px-3 sm:px-4 font-body text-xs sm:text-sm tracking-wider-custom uppercase transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === 'settings'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Settings
                    </button>
                </div>

                {/* Orders Tab */}
                {activeTab === 'orders' ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-6"
                    >
                        {/* Header with Refresh */}
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-2xl text-foreground italic">Order Management</h2>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { fetchOrders(); fetchOrderStats(); }}
                                className="rounded-sm"
                            >
                                <RefreshCw className={`w-4 h-4 mr-2 ${ordersLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                        </div>

                        {/* Order Stats */}
                        {orderStats && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
                                <div className="p-3 sm:p-4 bg-secondary/50 rounded-lg">
                                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                        <Package className="w-4 h-4" />
                                        <span className="font-body text-xs uppercase">Total</span>
                                    </div>
                                    <p className="font-display text-xl sm:text-2xl text-foreground">{orderStats.total_orders}</p>
                                </div>
                                <div className="p-3 sm:p-4 bg-amber-500/10 rounded-lg">
                                    <div className="flex items-center gap-2 text-amber-600 mb-1">
                                        <Clock className="w-4 h-4" />
                                        <span className="font-body text-xs uppercase">Pending</span>
                                    </div>
                                    <p className="font-display text-xl sm:text-2xl text-amber-600">{orderStats.pending_orders}</p>
                                </div>
                                <div className="p-3 sm:p-4 bg-blue-500/10 rounded-lg">
                                    <div className="flex items-center gap-2 text-blue-600 mb-1">
                                        <Loader2 className="w-4 h-4" />
                                        <span className="font-body text-xs uppercase">Processing</span>
                                    </div>
                                    <p className="font-display text-xl sm:text-2xl text-blue-600">{orderStats.processing_orders}</p>
                                </div>
                                <div className="p-3 sm:p-4 bg-green-500/10 rounded-lg">
                                    <div className="flex items-center gap-2 text-green-600 mb-1">
                                        <Truck className="w-4 h-4" />
                                        <span className="font-body text-xs uppercase">Shipped</span>
                                    </div>
                                    <p className="font-display text-xl sm:text-2xl text-green-600">{orderStats.shipped_orders}</p>
                                </div>
                                <div className="p-3 sm:p-4 bg-accent/10 rounded-lg col-span-2 sm:col-span-1">
                                    <div className="flex items-center gap-2 text-accent mb-1">
                                        <DollarSign className="w-4 h-4" />
                                        <span className="font-body text-xs uppercase">Revenue</span>
                                    </div>
                                    <p className="font-display text-xl sm:text-2xl text-accent">${orderStats.total_revenue.toLocaleString()}</p>
                                </div>
                            </div>
                        )}

                        {/* Orders List */}
                        {ordersLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-12 bg-secondary/30 rounded-lg">
                                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                <p className="font-body text-lg text-foreground mb-2">No orders yet</p>
                                <p className="font-body text-sm text-muted-foreground">Orders will appear here when customers complete checkout.</p>
                                <p className="font-body text-xs text-muted-foreground mt-4">
                                    Test the checkout flow by adding items to cart and completing a purchase.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map(order => (
                                    <div key={order.id} className="bg-secondary/30 border border-border rounded-lg p-4 sm:p-6">
                                        {/* Order Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                                            <div>
                                                <p className="font-display text-lg text-foreground">{order.order_number}</p>
                                                <p className="font-body text-sm text-muted-foreground">
                                                    {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-start sm:items-end gap-2">
                                                <p className="font-display text-xl text-foreground">${order.total?.toLocaleString()}</p>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`px-2 py-0.5 text-xs rounded-full font-body ${
                                                        order.payment_status === 'paid' ? 'bg-green-500/20 text-green-600' :
                                                        order.payment_status === 'pending' ? 'bg-amber-500/20 text-amber-600' :
                                                        'bg-red-500/20 text-red-600'
                                                    }`}>
                                                        {order.payment_status === 'paid' ? '✓ Paid' : order.payment_status}
                                                    </span>
                                                    <span className={`px-2 py-0.5 text-xs rounded-full font-body ${
                                                        order.status === 'delivered' ? 'bg-green-500/20 text-green-600' :
                                                        order.status === 'shipped' ? 'bg-blue-500/20 text-blue-600' :
                                                        order.status === 'processing' ? 'bg-purple-500/20 text-purple-600' :
                                                        order.status === 'cancelled' ? 'bg-red-500/20 text-red-600' :
                                                        'bg-amber-500/20 text-amber-600'
                                                    }`}>
                                                        {order.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Customer & Shipping Info */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm">
                                            <div className="bg-secondary/50 p-3 rounded">
                                                <p className="font-body text-xs text-muted-foreground uppercase mb-1">Customer</p>
                                                <p className="font-body text-foreground font-medium">
                                                    {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                                                </p>
                                                <p className="font-body text-foreground">{order.shipping_address?.email}</p>
                                                {order.shipping_address?.phone && (
                                                    <p className="font-body text-muted-foreground">{order.shipping_address?.phone}</p>
                                                )}
                                            </div>
                                            <div className="bg-secondary/50 p-3 rounded">
                                                <p className="font-body text-xs text-muted-foreground uppercase mb-1">Ship To</p>
                                                <p className="font-body text-foreground">{order.shipping_address?.address_line1}</p>
                                                {order.shipping_address?.address_line2 && (
                                                    <p className="font-body text-foreground">{order.shipping_address?.address_line2}</p>
                                                )}
                                                <p className="font-body text-foreground">
                                                    {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}
                                                </p>
                                                <p className="font-body text-muted-foreground">{order.shipping_address?.country}</p>
                                            </div>
                                        </div>

                                        {/* Order Items */}
                                        <div className="border-t border-border pt-4 mb-4">
                                            <p className="font-body text-xs text-muted-foreground uppercase mb-2">
                                                Items ({order.items?.length})
                                            </p>
                                            <div className="space-y-2">
                                                {order.items?.map((item, idx) => (
                                                    <div key={idx} className="flex items-center gap-3 bg-secondary/50 p-2 rounded">
                                                        {item.image && (
                                                            <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                                        )}
                                                        <div className="flex-1">
                                                            <p className="font-body text-sm text-foreground">{item.name}</p>
                                                            <p className="font-body text-xs text-muted-foreground">
                                                                Qty: {item.quantity} × ${item.price.toLocaleString()}
                                                            </p>
                                                        </div>
                                                        <p className="font-body text-sm text-foreground">
                                                            ${(item.price * item.quantity).toLocaleString()}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            {/* Order Totals */}
                                            <div className="mt-3 pt-3 border-t border-border/50 space-y-1 text-sm">
                                                <div className="flex justify-between text-muted-foreground">
                                                    <span>Subtotal</span>
                                                    <span>${order.subtotal?.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between text-muted-foreground">
                                                    <span>Shipping</span>
                                                    <span>{order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost?.toFixed(2)}`}</span>
                                                </div>
                                                <div className="flex justify-between font-medium text-foreground">
                                                    <span>Total</span>
                                                    <span>${order.total?.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Update Actions */}
                                        {order.payment_status === 'paid' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                                            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-body text-sm text-muted-foreground">Update Status:</span>
                                                    <select
                                                        value={order.status}
                                                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                                        className="px-3 py-2 border border-border bg-background rounded-sm font-body text-sm"
                                                    >
                                                        <option value="processing">Processing</option>
                                                        <option value="shipped">Shipped</option>
                                                        <option value="delivered">Delivered</option>
                                                        <option value="cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {/* Delivered/Cancelled indicator */}
                                        {(order.status === 'delivered' || order.status === 'cancelled') && (
                                            <div className={`pt-4 border-t border-border text-center ${
                                                order.status === 'delivered' ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                <CheckCircle className="w-5 h-5 mx-auto mb-1" />
                                                <p className="font-body text-sm">
                                                    {order.status === 'delivered' ? 'Order Completed' : 'Order Cancelled'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                ) : activeTab === 'subscribers' ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-6"
                    >
                        {/* Header with Refresh and Export */}
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-2xl text-foreground italic">Newsletter Subscribers</h2>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchSubscribers}
                                    className="rounded-sm"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${subscribersLoading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={exportSubscribers}
                                    className="rounded-sm bg-accent hover:bg-accent/90"
                                >
                                    Export Emails
                                </Button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="bg-secondary/30 rounded-sm p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-accent" />
                                </div>
                                <div>
                                    <p className="font-display text-2xl text-foreground">{subscribers.length}</p>
                                    <p className="font-body text-sm text-muted-foreground">Total Subscribers</p>
                                </div>
                            </div>
                        </div>

                        {/* Subscribers List */}
                        {subscribersLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : subscribers.length === 0 ? (
                            <div className="text-center py-12 bg-secondary/30 rounded-sm">
                                <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                <p className="font-body text-muted-foreground">No subscribers yet</p>
                                <p className="font-body text-sm text-muted-foreground mt-2">
                                    Subscribers from your Coming Soon page will appear here.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-secondary/30 rounded-sm overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-secondary/50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-body text-xs uppercase text-muted-foreground tracking-wider">Email</th>
                                            <th className="px-4 py-3 text-left font-body text-xs uppercase text-muted-foreground tracking-wider hidden lg:table-cell">Note</th>
                                            <th className="px-4 py-3 text-left font-body text-xs uppercase text-muted-foreground tracking-wider hidden sm:table-cell">Subscribed</th>
                                            <th className="px-4 py-3 text-right font-body text-xs uppercase text-muted-foreground tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {subscribers.map((sub, index) => (
                                            <tr key={index} className="hover:bg-secondary/20">
                                                <td className="px-4 py-3">
                                                    <a 
                                                        href={`mailto:${sub.email}`}
                                                        className="font-body text-sm text-foreground hover:text-accent"
                                                    >
                                                        {sub.email}
                                                    </a>
                                                </td>
                                                <td className="px-4 py-3 hidden lg:table-cell">
                                                    <span className="font-body text-sm text-muted-foreground">
                                                        {sub.note || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 hidden sm:table-cell">
                                                    <span className="font-body text-sm text-muted-foreground">
                                                        {new Date(sub.subscribed_at).toLocaleDateString()}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => deleteSubscriber(sub.email)}
                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </motion.div>
                ) : activeTab === 'settings' ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-8"
                    >
                        {/* Carousel Settings */}
                        <div className="bg-secondary/30 rounded-sm p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Settings className="w-5 h-5 text-accent" />
                                <h2 className="font-display text-xl text-foreground italic">
                                    Featured Carousel Settings
                                </h2>
                            </div>
                            <p className="font-body text-sm text-muted-foreground mb-6">
                                Control the featured specimens carousel on your homepage.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <Label className="font-body text-sm mb-2 block">Autoplay Speed (seconds)</Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="2"
                                            max="15"
                                            value={carouselSettings.autoplaySpeed / 1000}
                                            onChange={(e) => setCarouselSettings(prev => ({
                                                ...prev,
                                                autoplaySpeed: parseInt(e.target.value) * 1000
                                            }))}
                                            className="rounded-sm w-24"
                                        />
                                        <span className="font-body text-sm text-muted-foreground">
                                            seconds between slides
                                        </span>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="font-body text-sm mb-2 block">Transition Duration (ms)</Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="200"
                                            max="1500"
                                            step="100"
                                            value={carouselSettings.transitionDuration}
                                            onChange={(e) => setCarouselSettings(prev => ({
                                                ...prev,
                                                transitionDuration: parseInt(e.target.value)
                                            }))}
                                            className="rounded-sm w-24"
                                        />
                                        <span className="font-body text-sm text-muted-foreground">
                                            milliseconds
                                        </span>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="font-body text-sm mb-2 block">Max Featured Items</Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            min="3"
                                            max="12"
                                            value={carouselSettings.maxSlides}
                                            onChange={(e) => setCarouselSettings(prev => ({
                                                ...prev,
                                                maxSlides: parseInt(e.target.value)
                                            }))}
                                            className="rounded-sm w-24"
                                        />
                                        <span className="font-body text-sm text-muted-foreground">
                                            specimens in carousel
                                        </span>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="font-body text-sm mb-2 block">Enable Autoplay</Label>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setCarouselSettings(prev => ({
                                                ...prev,
                                                enableAutoplay: !prev.enableAutoplay
                                            }))}
                                            className={`relative w-12 h-6 rounded-full transition-colors ${
                                                carouselSettings.enableAutoplay ? 'bg-accent' : 'bg-muted'
                                            }`}
                                        >
                                            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                                carouselSettings.enableAutoplay ? 'translate-x-7' : 'translate-x-1'
                                            }`} />
                                        </button>
                                        <span className="font-body text-sm text-muted-foreground">
                                            {carouselSettings.enableAutoplay ? 'Auto-advances slides' : 'Manual navigation only'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-6 pt-4 border-t border-border">
                                <Button 
                                    onClick={() => {
                                        // Save to localStorage for now
                                        localStorage.setItem('carouselSettings', JSON.stringify(carouselSettings));
                                        toast.success('Carousel settings saved!');
                                    }}
                                    className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-sm"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Carousel Settings
                                </Button>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="bg-secondary/30 rounded-sm p-6">
                            <h2 className="font-display text-xl text-foreground italic mb-4">
                                Main Collection Image
                            </h2>
                            <p className="font-body text-sm text-muted-foreground mb-4">
                                This is the main image shown on the homepage with the interactive mineral hotspots.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-6 items-start">
                                <div className="w-full sm:w-64 h-40 bg-secondary rounded-sm overflow-hidden border-2 border-dashed border-border">
                                    <img
                                        src={siteSettings?.heroImage || 'https://customer-assets.emergentagent.com/job_motion-design-6/artifacts/szllhbry_Untitled.png'}
                                        alt="Hero"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleHeroImageUpload}
                                        className="hidden"
                                        id="hero-upload"
                                        disabled={isUploading}
                                    />
                                    <label
                                        htmlFor="hero-upload"
                                        className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm cursor-pointer hover:bg-primary/90 transition-colors font-body text-sm ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isUploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Upload className="w-4 h-4" />
                                        )}
                                        Upload New Image
                                    </label>
                                    <p className="font-body text-xs text-muted-foreground mt-2">
                                        Recommended: High resolution image of your collection display
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Logo */}
                        <div className="bg-secondary/30 rounded-sm p-6">
                            <h2 className="font-display text-xl text-foreground italic mb-4">
                                Site Logo
                            </h2>
                            <p className="font-body text-sm text-muted-foreground mb-4">
                                Your logo appears in the header and footer of the site.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-6 items-start">
                                <div className="w-32 h-16 bg-secondary rounded-sm overflow-hidden border-2 border-dashed border-border flex items-center justify-center">
                                    <img
                                        src={siteSettings?.logo || 'https://customer-assets.emergentagent.com/job_motion-design-6/artifacts/nd6mstgk_IMG_8935%20%281%29.png'}
                                        alt="Logo"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        className="hidden"
                                        id="logo-upload"
                                        disabled={isUploading}
                                    />
                                    <label
                                        htmlFor="logo-upload"
                                        className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm cursor-pointer hover:bg-primary/90 transition-colors font-body text-sm ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isUploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Upload className="w-4 h-4" />
                                        )}
                                        Upload New Logo
                                    </label>
                                    <p className="font-body text-xs text-muted-foreground mt-2">
                                        Recommended: PNG with transparent background
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Item List */}
                        <div className="lg:col-span-1 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-display text-xl text-foreground italic">
                                    {activeTab === 'collection' ? 'Specimens' : 'Shop Items'}
                                </h2>
                                <Button onClick={handleAddNew} size="sm" className="rounded-sm">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add New
                                </Button>
                            </div>

                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                                {currentItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`flex items-center gap-3 p-3 rounded-sm border transition-colors cursor-pointer ${
                                            editingItem?.id === item.id
                                                ? 'border-primary bg-accent/50'
                                                : 'border-border hover:border-primary/50'
                                        }`}
                                        onClick={() => handleEdit(item, activeTab)}
                                    >
                                        <div className="w-12 h-12 bg-secondary rounded-sm overflow-hidden flex-shrink-0">
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Image className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-body text-sm text-foreground truncate">
                                                {item.name}
                                            </p>
                                            <p className="font-body text-xs text-muted-foreground truncate">
                                                {item.location}
                                            </p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(item.id, activeTab);
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Edit Form */}
                        <div className="lg:col-span-2">
                            {(editingItem || isAddingNew) ? (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="bg-secondary/30 rounded-sm p-6"
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <h2 className="font-display text-xl text-foreground italic">
                                            {isAddingNew ? 'Add New Item' : 'Edit Item'}
                                        </h2>
                                        <Button variant="ghost" size="icon" onClick={handleCancel}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Image Upload */}
                                        <div className="md:col-span-2">
                                            <Label className="font-body text-sm mb-2 block">Image *</Label>
                                            <div className="flex gap-4 items-start">
                                                <div className="w-32 h-32 bg-secondary rounded-sm overflow-hidden flex-shrink-0 border-2 border-dashed border-border">
                                                    {formData.image ? (
                                                        <img
                                                            src={formData.image}
                                                            alt="Preview"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                                                            <Image className="w-8 h-8 mb-1" />
                                                            <span className="text-xs">No image</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        className="hidden"
                                                        id="image-upload"
                                                        disabled={isUploading}
                                                    />
                                                    <label
                                                        htmlFor="image-upload"
                                                        className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm cursor-pointer hover:bg-primary/90 transition-colors font-body text-sm ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        {isUploading ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Upload className="w-4 h-4" />
                                                        )}
                                                        Upload Photo
                                                    </label>
                                                    <p className="font-body text-xs text-muted-foreground mt-2">
                                                        Or paste an image URL:
                                                    </p>
                                                    <Input
                                                        name="image"
                                                        value={formData.image}
                                                        onChange={handleInputChange}
                                                        placeholder="https://..."
                                                        className="mt-1 rounded-sm text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Gallery Images */}
                                        <div className="md:col-span-2">
                                            <Label className="font-body text-sm mb-2 block">Gallery Images</Label>
                                            <p className="font-body text-xs text-muted-foreground mb-3">
                                                Add multiple photos to show different angles of this specimen.
                                            </p>
                                                
                                                {/* Existing gallery images */}
                                                {formData.images && formData.images.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        {formData.images.map((img, index) => (
                                                            <div key={index} className="relative group">
                                                                <div className="w-20 h-20 bg-secondary rounded-sm overflow-hidden border border-border">
                                                                    <img
                                                                        src={img}
                                                                        alt={`Gallery ${index + 1}`}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeGalleryImage(index)}
                                                                    className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Add gallery image button */}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleGalleryImageUpload}
                                                    className="hidden"
                                                    id="gallery-upload"
                                                    disabled={isUploading}
                                                />
                                                <label
                                                    htmlFor="gallery-upload"
                                                    className={`inline-flex items-center gap-2 px-4 py-2 border border-dashed border-border bg-secondary/50 text-muted-foreground rounded-sm cursor-pointer hover:bg-secondary hover:text-foreground transition-colors font-body text-sm ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isUploading ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Plus className="w-4 h-4" />
                                                    )}
                                                    Add Gallery Image
                                                </label>
                                            </div>

                                        {/* Videos */}
                                        <div className="md:col-span-2">
                                                <Label className="font-body text-sm mb-2 block flex items-center gap-2">
                                                    <Film className="w-4 h-4 text-accent" />
                                                    Specimen Videos
                                                </Label>
                                                <p className="font-body text-xs text-muted-foreground mb-3">
                                                    Upload high-quality videos to showcase sparkle, light play, and 3D form. Supports MP4, MOV, WebM (max 500MB).
                                                </p>
                                                
                                                {/* Existing videos */}
                                                {formData.videos && formData.videos.length > 0 && (
                                                    <div className="flex flex-wrap gap-3 mb-4">
                                                        {formData.videos.map((video, index) => (
                                                            <div key={index} className="relative group">
                                                                <div className="w-32 h-24 bg-navy rounded-sm overflow-hidden border border-border">
                                                                    <video
                                                                        src={video}
                                                                        className="w-full h-full object-cover"
                                                                        muted
                                                                        preload="metadata"
                                                                    />
                                                                    <div className="absolute inset-0 flex items-center justify-center bg-navy/40">
                                                                        <Video className="w-6 h-6 text-white" />
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeVideo(index)}
                                                                    className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                                                                >
                                                                    ×
                                                                </button>
                                                                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-navy/80 text-white text-xs rounded">
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Add video button */}
                                                <input
                                                    type="file"
                                                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                                                    onChange={handleVideoUpload}
                                                    className="hidden"
                                                    id="video-upload"
                                                    disabled={isUploading}
                                                />
                                                <label
                                                    htmlFor="video-upload"
                                                    className={`inline-flex items-center gap-2 px-4 py-2 border border-dashed border-accent/50 bg-accent/10 text-accent rounded-sm cursor-pointer hover:bg-accent/20 transition-colors font-body text-sm ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isUploading ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Video className="w-4 h-4" />
                                                    )}
                                                    Upload Video
                                                </label>
                                            </div>

                                        {/* Name */}
                                        <div>
                                            <Label className="font-body text-sm mb-2 block">Name *</Label>
                                            <Input
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Amethyst Cluster"
                                                className="rounded-sm"
                                            />
                                        </div>

                                        {/* Location */}
                                        <div>
                                            <Label className="font-body text-sm mb-2 block">Location</Label>
                                            <Input
                                                name="location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Minas Gerais, Brazil"
                                                className="rounded-sm"
                                            />
                                        </div>

                                        {/* Size */}
                                        <div>
                                            <Label className="font-body text-sm mb-2 block">Size (dimensions)</Label>
                                            <Input
                                                name="size"
                                                value={formData.size}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 12.5 x 8.3 x 6.2 cm"
                                                className="rounded-sm"
                                            />
                                        </div>

                                        {/* Weight */}
                                        <div>
                                            <Label className="font-body text-sm mb-2 block">Weight</Label>
                                            <Input
                                                name="weight"
                                                value={formData.weight}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 485g"
                                                className="rounded-sm"
                                            />
                                        </div>

                                        {/* Acquisition Date */}
                                        <div>
                                            <Label className="font-body text-sm mb-2 block">Acquisition Date</Label>
                                            <Input
                                                name="acquisitionDate"
                                                value={formData.acquisitionDate}
                                                onChange={handleInputChange}
                                                placeholder="e.g., March 2019"
                                                className="rounded-sm"
                                            />
                                        </div>

                                        {/* Rarity (Collection) or Category (Shop) */}
                                        {activeTab === 'collection' ? (
                                            <div>
                                                <Label className="font-body text-sm mb-2 block">Rarity (Optional)</Label>
                                                <select
                                                    name="rarity"
                                                    value={formData.rarity}
                                                    onChange={handleInputChange}
                                                    className="w-full h-10 px-3 border border-border bg-background rounded-sm font-body text-sm"
                                                >
                                                    {rarityOptions.map(opt => (
                                                        <option key={opt || 'none'} value={opt}>{opt || '— None —'}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <>
                                                <div>
                                                    <Label className="font-body text-sm mb-2 block">Category</Label>
                                                    <select
                                                        name="category"
                                                        value={formData.category}
                                                        onChange={handleInputChange}
                                                        className="w-full h-10 px-3 border border-border bg-background rounded-sm font-body text-sm"
                                                    >
                                                        {categoryOptions.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <Label className="font-body text-sm mb-2 block">Price ($)</Label>
                                                    <Input
                                                        name="price"
                                                        type="number"
                                                        value={formData.price}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., 150"
                                                        className="rounded-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="font-body text-sm mb-2 block">SKU (Stock Code)</Label>
                                                    <div className="flex gap-2">
                                                        <Input
                                                            name="sku"
                                                            value={formData.sku}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., CER-001"
                                                            className="rounded-sm flex-1"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={generateSKU}
                                                            className="flex-shrink-0"
                                                            title="Auto-generate SKU"
                                                        >
                                                            <Zap className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="font-body text-sm mb-2 block">Stock Quantity</Label>
                                                    <Input
                                                        name="stock_quantity"
                                                        type="number"
                                                        min="0"
                                                        value={formData.stock_quantity}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., 1"
                                                        className="rounded-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="font-body text-sm mb-2 block">Stock Status</Label>
                                                    <select
                                                        name="stock_status"
                                                        value={formData.stock_status}
                                                        onChange={handleInputChange}
                                                        className="w-full h-10 px-3 border border-border bg-background rounded-sm font-body text-sm"
                                                    >
                                                        <option value="in_stock">In Stock</option>
                                                        <option value="low_stock">Low Stock</option>
                                                        <option value="sold">Sold Out</option>
                                                        <option value="reserved">Reserved</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {/* Description */}
                                        <div className="md:col-span-2">
                                            <Label className="font-body text-sm mb-2 block">Description</Label>
                                            <Textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Describe this specimen..."
                                                rows={4}
                                                className="rounded-sm"
                                            />
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-3 mt-6 pt-6 border-t border-border">
                                        <Button onClick={handleSave} className="rounded-sm" disabled={isSaving}>
                                            {isSaving ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Save className="w-4 h-4 mr-2" />
                                            )}
                                            {isAddingNew ? 'Add Item' : 'Save Changes'}
                                        </Button>
                                        <Button variant="outline" onClick={handleCancel} className="rounded-sm" disabled={isSaving}>
                                            Cancel
                                        </Button>
                                    </div>
                                </motion.div>
                            ) : (
                                <div className="bg-secondary/30 rounded-sm p-12 text-center">
                                    <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="font-body text-muted-foreground mb-4">
                                        Select an item to edit or add a new one
                                    </p>
                                    <Button onClick={handleAddNew} className="rounded-sm">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add New {activeTab === 'collection' ? 'Specimen' : 'Shop Item'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
